import React, {
  ComponentType, ReactElement, ReactNode, useEffect, useRef,
} from "react"
import { ErrorBoundary as SentryErrorBoundary } from "@sentry/react"
import { useLocation } from "react-router-dom"
import ErrorView from "components/ErrorView"
import PageErrorView from "components/PageErrorView"
import PanelErrorView from "components/PanelErrorView"

type FallbackProps = { resetError: () => void }
type Fallback = (props: FallbackProps) => ReactElement

// Boundaries don't heal on their own. Without this, a crashed section stays
// crashed after the user navigates away (or presses Back) until a hard reload.
// Resetting from the fallback, instead of keying the boundary by pathname,
// keeps healthy subtrees mounted (and their state) on every navigation.
export const ResetOnNavigate = ({ resetError, children }: FallbackProps & {
  children: ReactNode
}) => {
  const { pathname, search } = useLocation()
  const location = `${pathname}${search}`
  const errorLocation = useRef(location)

  useEffect(() => {
    if (location !== errorLocation.current) {
      resetError()
    }
  }, [location])

  return <>{children}</>
}

// Sentry renders a function `fallback` as a component type
// (React.createElement(fallback, ...)), so every fallback below is created
// once, at module level or when a component is wrapped, never during render.
// An inline arrow would be a new type on every render: any re-render of the
// parent, which every navigation causes, would remount the fallback and
// ResetOnNavigate would never see the location change.
const makeFallback = (render: (resetError: () => void) => ReactNode): Fallback => {
  const Fallback = ({ resetError }: FallbackProps) => (
    <ResetOnNavigate resetError={resetError}>{render(resetError)}</ResetOnNavigate>
  )

  return Fallback
}

const Boundary = ({ name, fallback, children }: {
  name: string
  fallback: Fallback
  children: ReactNode
}) => (
  <SentryErrorBoundary
    // Sent to Sentry as the `boundary` tag, so an event says which part of
    // the app failed instead of "somewhere in the app".
    beforeCapture={(scope) => { scope.setTag("boundary", name) }}
    fallback={fallback}
  >
    {children}
  </SentryErrorBoundary>
)

const fallbacks = {
  // The whole app is gone. Last resort inside the router (MainRouter).
  fullscreen: makeFallback((resetError) => <ErrorView clearError={resetError} />),
  // One page or event tab failed, the navigation stays (RouteErrorBoundary).
  page: makeFallback((resetError) => <PageErrorView onRetry={resetError} />),
}

type Props = {
  children: ReactNode
  name: string
  variant?: keyof typeof fallbacks
}

const ErrorBoundary = ({ children, name, variant = "fullscreen" }: Props) => (
  <Boundary name={name} fallback={fallbacks[variant]}>{children}</Boundary>
)

type SectionOptions = {
  name: string
  // A shorter text for tight spots, e.g. "Stats unavailable."
  message?: string
  // Center the message in the area the section would fill, e.g. a pane.
  centered?: boolean
}

// One independent section of a page gets its own boundary, so a crash there
// costs only that section: `export default withSectionBoundary(GuestStatBar,
// { name: "guest-stats" })`.
export const withSectionBoundary = <P extends object>(
  Component: ComponentType<P>,
  { name, message, centered }: SectionOptions,
) => {
  const Fallback = makeFallback((resetError) => (
    <PanelErrorView onRetry={resetError} message={message} centered={centered} />
  ))

  const WithSectionBoundary = (props: P) => (
    <Boundary name={name} fallback={Fallback}>
      <Component {...props} />
    </Boundary>
  )
  WithSectionBoundary.displayName = `withSectionBoundary(${Component.displayName || Component.name})`

  return WithSectionBoundary
}

export default ErrorBoundary
